import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getDashboardStats } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Briefcase, 
  Store, 
  MapPin, 
  Building2, 
  FileSearch, 
  AlertCircle,
  CheckCircle,
  TestTube,
  Loader2,
  Database
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import api from '../lib/api';

export default function Dashboard() {
  const { t, isRTL, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const handleSeedDemo = async () => {
    if (!confirm(language === 'ar' ? 'سيتم حذف المشاريع الحالية وتوليد 10,000 مشروع جديد مع 300 مشروع مميز. متأكد؟' : 'This will delete existing projects and generate 10,000 new ones. Continue?')) return;
    setSeeding(true);
    try {
      const res = await api.post('/seed-demo-data');
      const d = res.data;
      toast.success(d.message || `${d.total_projects} projects created`);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
    setSeeding(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }

  const statCards = [
    { key: 'total_projects', value: stats?.total_projects || 0, icon: Briefcase, color: 'bg-blue-500' },
    { key: 'total_outlets', value: stats?.total_outlets || 0, icon: Store, color: 'bg-green-500' },
    { key: 'total_regions', value: stats?.total_regions || 0, icon: MapPin, color: 'bg-purple-500' },
    { key: 'total_cities', value: stats?.total_cities || 0, icon: Building2, color: 'bg-orange-500' },
    { key: 'pending_reviews', value: stats?.pending_reviews || 0, icon: FileSearch, color: 'bg-yellow-500' },
    { key: 'import_errors_count', value: stats?.import_errors || 0, icon: AlertCircle, color: 'bg-red-500' },
    { key: 'published_projects', value: stats?.published_projects || 0, icon: CheckCircle, color: 'bg-emerald-500' },
    { key: 'sample_projects', value: stats?.sample_projects || 0, icon: TestTube, color: 'bg-indigo-500' },
  ];

  const chartData = stats?.projects_by_region?.map(item => ({
    name: item.region_name,
    value: item.count
  })) || [];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Seed Demo Data Button */}
      <div className="flex justify-end">
        <Button onClick={handleSeedDemo} disabled={seeding} variant="outline"
          className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="seed-demo-btn">
          {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
          {seeding
            ? (language === 'ar' ? 'جارٍ التوليد...' : 'Generating...')
            : (language === 'ar' ? 'توليد بيانات تجريبية (10,000 مشروع)' : 'Seed Demo Data (10,000 projects)')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.key} className="bg-white border border-gray-200" data-testid={`stat-${stat.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">{t(stat.key)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Chart */}
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            {t('projects_by_region')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={chartData} 
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" stroke="#6B7280" fontSize={12} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="#6B7280" 
                    fontSize={11}
                    width={120}
                    tick={{ textAnchor: isRTL ? 'start' : 'end' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" fill="#0A4C3A" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center text-gray-500">
              {t('no_data')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
